import { getObjectSnapPoints } from './getObjectSnapPoints';
import { getCanvasSnapPoints } from './getCanvasSnapPoints';
import { findNearestSnapPoint } from './findNearestSnapPoint';
import editorConfig from '~config/editorConfig';

/**
 * Calculate snap position for an object based on its current position
 * @param {fabric.Object} object - The object being moved
 * @param {fabric.Canvas} canvas - The fabric canvas
 * @param {Object} config - Configuration options
 * @returns {Object} New position {x, y} and snap info
 */
export const calculateObjectSnapPosition = (object, canvas, config = {}) => {
  const cfg = { ...editorConfig.snapSettings, ...config };
  const objectPoints = getObjectSnapPoints(object);

  let snapX = null;
  let snapY = null;
  let snapInfoX = null;
  let snapInfoY = null;

  // Get all snap points
  const verticalSnapPoints = getCanvasSnapPoints(
    canvas,
    'vertical',
    object,
    cfg
  );
  const horizontalSnapPoints = getCanvasSnapPoints(
    canvas,
    'horizontal',
    object,
    cfg
  );

  // Check vertical snapping (for X position)
  const verticalCandidates = [
    { current: objectPoints.left, type: 'left', offset: 0 },
    { current: objectPoints.right, type: 'right', offset: -objectPoints.width },
    {
      current: objectPoints.centerX,
      type: 'centerX',
      offset: -objectPoints.width / 2,
    },
  ];

  for (const candidate of verticalCandidates) {
    if (
      (candidate.type === 'centerX' && !cfg.enableCenterSnapping) ||
      (candidate.type !== 'centerX' && !cfg.enableEdgeSnapping)
    ) {
      continue;
    }

    const nearest = findNearestSnapPoint(
      candidate.current,
      verticalSnapPoints,
      cfg.threshold
    );

    if (nearest) {
      snapX = objectPoints.left + (nearest.position - candidate.current);
      snapInfoX = { ...nearest, edge: candidate.type };
      break;
    }
  }

  // Check horizontal snapping (for Y position)
  const horizontalCandidates = [
    { current: objectPoints.top, type: 'top', offset: 0 },
    {
      current: objectPoints.bottom,
      type: 'bottom',
      offset: -objectPoints.height,
    },
    {
      current: objectPoints.centerY,
      type: 'centerY',
      offset: -objectPoints.height / 2,
    },
  ];

  for (const candidate of horizontalCandidates) {
    if (
      (candidate.type === 'centerY' && !cfg.enableCenterSnapping) ||
      (candidate.type !== 'centerY' && !cfg.enableEdgeSnapping)
    ) {
      continue;
    }

    const nearest = findNearestSnapPoint(
      candidate.current,
      horizontalSnapPoints,
      cfg.threshold
    );

    if (nearest) {
      snapY = objectPoints.top + (nearest.position - candidate.current);
      snapInfoY = { ...nearest, edge: candidate.type };
      break;
    }
  }

  return {
    x: snapX !== null ? snapX : objectPoints.left,
    y: snapY !== null ? snapY : objectPoints.top,
    snappedX: snapX !== null,
    snappedY: snapY !== null,
    snapInfoX,
    snapInfoY,
  };
};
